import { APP_CLINIC } from "core/consts";
import { Env } from "core/types";
import { Button, ConnectedInput, ConnectedPasswordInput, Spinner } from "ds/ui";
import { LockIcon } from "lucide-react";
import { useTranslations } from "translations";
import {
  LoginErrorState,
  LoginView,
} from "../CareproviderOnboardingLoginPage/useHandleLogin";
import { LoginPageVariants } from "./classes";
import { LoginPageLayout } from "./LoginPageLayout";

export type LoginPageV2Props = LoginPageVariants & {
  challengeTimestamp: string | undefined;
  contactUsUrl?: string;
  env: Env;
  goToForgotPassword: string;
  goToProductionUrl: string;
  isEmailInputLoading?: boolean;
  isLoading: boolean;
  isSingleSignOn?: boolean;
  loginErrors?: LoginErrorState;
  loginView: LoginView;
  onBackToLogin: () => void;
  onChangeEmail?: (value: string) => void;
  onSubmit: () => void;
  otherAppUrl?: string;
  resetError: () => void;
};

export function LoginPageV2({
  app = APP_CLINIC,
  challengeTimestamp,
  contactUsUrl,
  env,
  goToForgotPassword,
  goToProductionUrl,
  isEmailInputLoading,
  isLoading,
  isSingleSignOn,
  loginErrors,
  loginView = "login",
  onBackToLogin,
  onChangeEmail,
  onSubmit,
  otherAppUrl,
  resetError,
}: LoginPageV2Props) {
  const translations = useTranslations();
  const isLogin = loginView === "login";

  return (
    <LoginPageLayout
      app={app}
      autoFocus="container"
      challengeField={
        <ConnectedInput
          autoComplete="off"
          errorMessage={loginErrors?.challenge}
          isInvalid={!!loginErrors?.challenge}
          label={translations.login.loginPage.verificationCode}
          name="challenge"
          placeholder={translations.login.loginPage.verificationCodePlaceholder}
          required
          sideMutation={() => resetError()}
          type={isLogin ? "hidden" : "text"}
        />
      }
      challengeTimestamp={challengeTimestamp}
      contactUsUrl={contactUsUrl}
      emailField={
        <ConnectedInput
          autoComplete="email"
          endContent={
            isEmailInputLoading ? (
              <Spinner size="sm" testId="email-input-spinner" />
            ) : undefined
          }
          errorMessage={loginErrors?.email}
          isInvalid={!!loginErrors?.email}
          label={translations.login.loginPage.email}
          name="email"
          placeholder={translations.login.loginPage.emailPlaceholder}
          required
          sideMutation={() => resetError()}
          type={isLogin ? "email" : "hidden"}
          onValueChange={onChangeEmail}
        />
      }
      env={env}
      goToForgotPassword={goToForgotPassword}
      goToProductionUrl={goToProductionUrl}
      isLoading={isLoading}
      isSingleSignOn={isSingleSignOn}
      loginErrors={loginErrors}
      loginView={loginView}
      otherAppUrl={otherAppUrl}
      passwordField={
        <ConnectedPasswordInput
          autoComplete={isLogin && !isSingleSignOn ? "current-password" : "off"}
          elementName="password"
          errorMessage={loginErrors?.password}
          isInvalid={!!loginErrors?.password}
          label={translations.login.loginPage.password}
          placeholder={translations.login.loginPage.passwordPlaceholder}
          required
          sideMutation={() => resetError()}
          type={isLogin && !isSingleSignOn ? "password" : "hidden"}
        />
      }
      ssoInfo={
        isSingleSignOn && (
          <div
            aria-live="polite"
            className="flex max-w-fit items-center space-x-2 rounded-md border border-success bg-success-light py-1.5 pr-5 pl-3 text-success-dark"
            role="alert"
          >
            <LockIcon aria-hidden="true" size={14} />{" "}
            <p className="text-sm">{translations.login.loginPage.ssoInfo}</p>
          </div>
        )
      }
      submit={
        <Button
          color="primary"
          fullWidth
          id={
            isLogin
              ? isSingleSignOn
                ? "login_sso"
                : "login"
              : "login_challenge"
          }
          isLoading={isLoading}
          size="lg"
          type="submit"
          variant="solid"
          onPress={onSubmit}
        >
          {translations.login.loginPage.loginButton}
        </Button>
      }
      onBackToLogin={onBackToLogin}
    />
  );
}
